import type {AiQueryCapabilityResult, AiQueryToolCall} from 'views/admin/adminAiQueryApi';

import type {AiQueryTraceEntry} from './aiQueryConversation';

/** Where a document read_document opens lives, for its step's logo. */
export type DocumentSource = 'confluence' | 'paper' | 'drive' | 'help_center' | 'dropbox';

type Row = Record<string, unknown>;

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/**
 * From read_document's Dash connector ("confluenceCloud", "dashhelpcenter"), a
 * search_docs hit's source ("paper", "help_center"), or else the link itself.
 */
export const documentSource = (source: unknown, url: unknown): DocumentSource | undefined => {
  const from = text(source).toLowerCase();
  const link = text(url).toLowerCase();
  if (from.startsWith('confluence') || link.includes('atlassian.net/wiki')) return 'confluence';
  if (
    from.startsWith('paper') ||
    link.includes('paper.dropbox.com') ||
    /\.paper(\?|#|$)/.test(link)
  )
    return 'paper';
  if (from.includes('drive') || from.includes('google') || /(docs|drive)\.google\.com/.test(link))
    return 'drive';
  if (from === 'help_center' || from === 'dashhelpcenter' || link.includes('help.dropbox.com'))
    return 'help_center';
  if (from.startsWith('dropbox') || link.includes('dropbox.com')) return 'dropbox';
  return undefined;
};

/** Every document the turn's search_docs calls found, in order. */
export const searchDocsHits = (
  trace: AiQueryTraceEntry[],
  datasets?: Record<string, AiQueryCapabilityResult>
): Row[] =>
  trace.flatMap((entry) =>
    entry.kind === 'result' && entry.result.name === 'search_docs'
      ? ((entry.result.handle ? datasets?.[entry.result.handle]?.items : undefined) ??
        entry.result.items ??
        [])
      : []
  );

/**
 * The document a read_document call opens: its title and where it lives. A
 * search_docs hit it opens (by doc_id or link) names it as soon as the call
 * starts (`fromSearch`); otherwise the title comes from what it read.
 */
export const readDocumentTarget = (
  call: AiQueryToolCall,
  items: Row[],
  searchHits: Row[]
): {title?: string; source?: DocumentSource; fromSearch: boolean} => {
  const docId = text(call.args?.doc_id);
  const url = text(call.args?.url);
  const hit = searchHits.find(
    (row) => (docId && text(row.doc_id) === docId) || (url && text(row.url) === url)
  );
  const read = items.find((row) => text(row.title) || text(row.source) || text(row.url));
  const hitTitle = text(hit?.title);
  const title = hitTitle || text(read?.title) || undefined;
  return {
    title,
    source:
      documentSource(read?.source, read?.url) ??
      documentSource(hit?.source, hit?.url) ??
      documentSource(undefined, url),
    fromSearch: Boolean(hitTitle),
  };
};
